import { createEffect, createEvent, createStore, sample } from 'effector';
import { projectLoaded } from '../../entities/project';
import { tourStarted } from '../../features/onboarding';
import { safeLocalStorage } from '../../shared/lib/safe-storage';

/*
 * The help center: one dialog with three tabs — what Kasora is and why it's worth it, a short
 * tutorial, and the keyboard shortcuts (`?`). Opened from the Help menu, the palette and `?`, and
 * once by itself for someone new, when their first project opens. Its "Take the tour" starts the
 * coach-mark tour (features/onboarding), which no longer starts on its own.
 */

export const HELP_TABS = ['welcome', 'tutorial', 'shortcuts'] as const;
export type HelpTab = (typeof HELP_TABS)[number];

/** The tutorial's steps, in order. */
export const TUTORIAL_STEPS = [
  'projects',
  'items',
  'assumptions',
  'charts',
  'compare',
  'share',
] as const;
export type TutorialStep = (typeof TUTORIAL_STEPS)[number];

/** Opens the help center on a tab. */
export const helpOpened = createEvent<HelpTab>();
export const helpClosed = createEvent();
export const helpTabChanged = createEvent<HelpTab>();
export const tutorialStepChanged = createEvent<number>();
/** "Take the tour": closes the help center and starts the coach marks. */
export const tourRequested = createEvent();

/** The open tab, or null when the help center is closed. */
export const $helpTab = createStore<HelpTab | null>(null)
  .on(helpOpened, (_, tab) => tab)
  .on(helpTabChanged, (current, tab) => (current === null ? current : tab))
  .reset(helpClosed, tourRequested);
export const $helpOpen = $helpTab.map((tab) => tab !== null);

export const $tutorialStep = createStore(0).on(tutorialStepChanged, (_, step) =>
  Math.min(TUTORIAL_STEPS.length - 1, Math.max(0, step)),
);

sample({ clock: tourRequested, target: tourStarted });

// ── First visit ───────────────────────────────────────────────────────────────

const WELCOME_KEY = 'finsim:welcome-seen';
/** Set by the tour before the help center existed: those visitors aren't new. */
const TOUR_KEY = 'finsim:tour-done';

/** Someone new: no welcome or tour seen in this browser, and not an automated test browser. */
export const isFirstVisit = (): boolean =>
  !navigator.webdriver &&
  safeLocalStorage.getItem(WELCOME_KEY) !== '1' &&
  safeLocalStorage.getItem(TOUR_KEY) !== '1';

const markWelcomeSeenFx = createEffect(() => {
  safeLocalStorage.setItem(WELCOME_KEY, '1');
});

const firstProjectOpen = sample({ clock: projectLoaded, filter: isFirstVisit });
sample({ clock: firstProjectOpen, fn: (): HelpTab => 'welcome', target: helpOpened });
sample({ clock: firstProjectOpen, target: markWelcomeSeenFx });
